import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalQuery } from "../api/useGlobalQuery";
import { useAppData } from "../state/AppData";
import { FIRST_CALCULATION_TEXT } from "../lib/definitions/analysis";
import { colorFor } from "../lib/definitions/palette";
import { MESHCORE_ROLE_CATEGORY, MESHCORE_ROLE_LABEL, normalizeMeshCoreRole, roleShade, type MeshCoreRole } from "../lib/definitions/protocols";
import { navigate } from "../lib/router";
import { createLayout, type Layout } from "../lib/forceLayout";
import {
  countFraction,
  countText,
  decodeGraph,
  defaultMinCount,
  edgeOpacity,
  edgeWidth,
  filterGraph,
  GRAPH_DESC,
  GRAPH_TITLE,
  GRAPH_WINDOWS,
  isPending,
  LAYOUT_TIP,
  MIN_OBS_TIP,
  minCountToShow,
  neighbourIndexes,
  nodeRadius,
  NO_LINKS_TEXT,
  searchNodes,
  sliderStops,
  droppedText,
  unplacedText,
  type GraphEdge,
  type GraphModel,
  type GraphWindow,
  type NeighbourGraphPayload,
  type NeighbourGraphResponse,
  type VisibleGraph,
} from "../lib/neighbourGraph";
import { SectionCard } from "./SectionCard";
import { NeighbourGraphMap } from "./NeighbourGraphMap";
import { NodeTip } from "./NeighbourGraphTip";

// Analysis > MeshCore > Neighbour graph. The graph is stored by the background pass; this only reads and draws it.
// Canvas drawing (about 800 nodes and several thousand links stay smooth), a force layout that settles and stops.

type LayoutMode = "graph" | "map";
interface View { k: number; tx: number; ty: number }

const BUCKETS = 8;
const MAX_LABELS = 12;
const FRAME_BUDGET_MS = 10;

function cssVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

export function NeighbourGraphTab() {
  const { effectiveTheme } = useAppData();
  const [windowKey, setWindowKey] = useState<GraphWindow>("24h");
  const q = useGlobalQuery<NeighbourGraphResponse>(
    ["neighbourGraph", "meshcore", windowKey], `/api/audit/neighbour-graph?window=${windowKey}`, 60000,
  );
  const data = q.data;
  const payload: NeighbourGraphPayload | null = data && !isPending(data) ? data : null;
  return (
    <div className="talkers-tab">
      <div className="node-list-toolbar">
        <div className="theme-toggle" role="group" aria-label="Window">
          {GRAPH_WINDOWS.map((w) => (
            <button key={w} type="button" data-active={windowKey === w} onClick={() => setWindowKey(w)}>
              {w}
            </button>
          ))}
        </div>
      </div>
      {data === null && q.loading && <div className="empty-state">Loading…</div>}
      {data === null && !q.loading && q.error && <div className="node-detail-note">Not available right now.</div>}
      {data !== null && isPending(data) && <div className="node-detail-note">{FIRST_CALCULATION_TEXT}</div>}
      {payload && (
        <SectionCard title={GRAPH_TITLE} description={GRAPH_DESC}>
          <GraphView key={windowKey} payload={payload} theme={effectiveTheme} />
        </SectionCard>
      )}
    </div>
  );
}

function GraphView({ payload, theme }: { payload: NeighbourGraphPayload; theme: "light" | "dark" }) {
  const model: GraphModel = useMemo(() => decodeGraph(payload), [payload]);
  const stops = useMemo(() => sliderStops(model.maxCount), [model]);
  const [minCount, setMinCount] = useState<number>(() => defaultMinCount(model));
  const [mode, setMode] = useState<LayoutMode>("graph");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [size, setSize] = useState({ w: 800, h: 520 });

  const vis: VisibleGraph = useMemo(() => filterGraph(model, minCount), [model, minCount]);
  const maxDegree = useMemo(() => vis.nodeIdx.reduce((m, i) => Math.max(m, vis.degree[i]), 0), [vis]);
  const roleColor = useCallback(
    (role: string | null) => colorFor(roleShade("meshcore", MESHCORE_ROLE_CATEGORY[normalizeMeshCoreRole(role)]), theme),
    [theme],
  );

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const posX = useRef<Float64Array>(new Float64Array(0));
  const posY = useRef<Float64Array>(new Float64Array(0));
  const view = useRef<View>({ k: 1, tx: 0, ty: 0 });
  const moved = useRef(false);
  const centreOn = useRef<number | null>(null);
  const hoverRef = useRef<number | null>(null);
  const selRef = useRef<number | null>(null);
  const drawQueued = useRef(false);
  const layoutRef = useRef<Layout | null>(null);
  const live = useRef({ model, vis, maxDegree, theme, size, mode, roleColor });
  live.current = { model, vis, maxDegree, theme, size, mode, roleColor };

  // --- drawing ---------------------------------------------------------------------------------------------------
  const draw = useCallback(() => {
    drawQueued.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { model: m, vis: v, maxDegree: maxDeg, size: sz, roleColor: colorOf } = live.current;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, sz.w, sz.h);
    const { k, tx, ty } = view.current;
    const xs = posX.current, ys = posY.current;
    const sx = (i: number) => xs[i] * k + tx;
    const sy = (i: number) => ys[i] * k + ty;
    const ink = cssVar(canvas, "--ink", "#111");
    const inkSecondary = cssVar(canvas, "--ink-secondary", "#555");
    const surface = cssVar(canvas, "--surface", "#fff");
    const focus = hoverRef.current ?? selRef.current;
    const placed = (i: number) => Number.isFinite(xs[i]);

    // links
    ctx.lineCap = "round";
    if (focus === null) {
      const buckets: GraphEdge[][] = Array.from({ length: BUCKETS }, () => []);
      for (const e of v.edges) {
        if (!placed(e.a) || !placed(e.b)) continue;
        buckets[Math.min(BUCKETS - 1, Math.floor(countFraction(e.count, m.maxCount) * BUCKETS))].push(e);
      }
      buckets.forEach((list, b) => {
        if (list.length === 0) return;
        const c = (b + 0.5) / BUCKETS;
        const sample = Math.round(Math.pow(m.maxCount, c));
        ctx.globalAlpha = edgeOpacity(sample, m.maxCount);
        ctx.lineWidth = edgeWidth(sample, m.maxCount);
        ctx.strokeStyle = inkSecondary;
        ctx.beginPath();
        for (const e of list) {
          ctx.moveTo(sx(e.a), sy(e.a));
          ctx.lineTo(sx(e.b), sy(e.b));
        }
        ctx.stroke();
      });
    } else {
      ctx.globalAlpha = 0.05;
      ctx.lineWidth = 0.7;
      ctx.strokeStyle = inkSecondary;
      ctx.beginPath();
      for (const e of v.edges) {
        if (e.a === focus || e.b === focus || !placed(e.a) || !placed(e.b)) continue;
        ctx.moveTo(sx(e.a), sy(e.a));
        ctx.lineTo(sx(e.b), sy(e.b));
      }
      ctx.stroke();
      ctx.strokeStyle = ink;
      for (const e of v.edges) {
        if ((e.a !== focus && e.b !== focus) || !placed(e.a) || !placed(e.b)) continue;
        ctx.globalAlpha = Math.max(0.55, edgeOpacity(e.count, m.maxCount));
        ctx.lineWidth = Math.max(1.2, edgeWidth(e.count, m.maxCount));
        ctx.beginPath();
        ctx.moveTo(sx(e.a), sy(e.a));
        ctx.lineTo(sx(e.b), sy(e.b));
        ctx.stroke();
      }
    }

    // nodes, the most linked on top
    const near = focus === null ? null : neighbourIndexes(v.edges, focus);
    const zoomScale = Math.min(2, Math.max(0.8, Math.pow(k, 0.35)));
    const order = [...v.nodeIdx].sort((a, b) => v.degree[a] - v.degree[b]);
    for (const i of order) {
      if (!placed(i)) continue;
      const lit = near === null || i === focus || near.has(i);
      ctx.globalAlpha = lit ? 1 : 0.18;
      const r = nodeRadius(v.degree[i], maxDeg) * zoomScale;
      ctx.beginPath();
      ctx.arc(sx(i), sy(i), r, 0, Math.PI * 2);
      ctx.fillStyle = colorOf(m.nodes[i].role);
      ctx.fill();
      ctx.lineWidth = i === focus ? 2.5 : 1;
      ctx.strokeStyle = i === focus ? ink : surface;
      ctx.stroke();
    }

    // labels: the focused node and its strongest neighbours
    if (focus !== null && placed(focus)) {
      const incident = v.edges
        .filter((e) => (e.a === focus || e.b === focus) && placed(e.a) && placed(e.b))
        .sort((a, b) => b.count - a.count)
        .slice(0, MAX_LABELS);
      const names = [focus, ...incident.map((e) => (e.a === focus ? e.b : e.a))];
      ctx.globalAlpha = 1;
      ctx.font = "11px system-ui, sans-serif";
      ctx.textBaseline = "middle";
      ctx.lineJoin = "round";
      for (const i of names) {
        const label = m.nodes[i].name;
        const r = nodeRadius(v.degree[i], maxDeg) * zoomScale;
        ctx.lineWidth = 3.5;
        ctx.strokeStyle = surface;
        ctx.strokeText(label, sx(i) + r + 3, sy(i));
        ctx.fillStyle = ink;
        ctx.fillText(label, sx(i) + r + 3, sy(i));
      }
    }
    ctx.globalAlpha = 1;
  }, []);

  const requestDraw = useCallback(() => {
    if (drawQueued.current) return;
    drawQueued.current = true;
    requestAnimationFrame(draw);
  }, [draw]);

  const centreNow = useCallback((idx: number): boolean => {
    const x = posX.current[idx], y = posY.current[idx];
    if (!Number.isFinite(x)) return false;
    const k = Math.max(view.current.k, 2.2);
    view.current = { k, tx: live.current.size.w / 2 - x * k, ty: live.current.size.h / 2 - y * k };
    return true;
  }, []);

  const fitView = useCallback(() => {
    const { vis: v, size: sz } = live.current;
    const xs = posX.current, ys = posY.current;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const i of v.nodeIdx) {
      if (!Number.isFinite(xs[i])) continue;
      minX = Math.min(minX, xs[i]);
      maxX = Math.max(maxX, xs[i]);
      minY = Math.min(minY, ys[i]);
      maxY = Math.max(maxY, ys[i]);
    }
    if (!Number.isFinite(minX)) return;
    const pad = 28;
    const bw = Math.max(maxX - minX, 1), bh = Math.max(maxY - minY, 1);
    const k = Math.min(12, Math.max(0.05, Math.min((sz.w - 2 * pad) / bw, (sz.h - 2 * pad) / bh)));
    view.current = { k, tx: sz.w / 2 - ((minX + maxX) / 2) * k, ty: sz.h / 2 - ((minY + maxY) / 2) * k };
  }, []);

  /** Until the reader moves the view: centre on the searched node, else fit everything. */
  const autoView = useCallback(() => {
    if (moved.current) return;
    if (centreOn.current !== null && centreNow(centreOn.current)) return;
    fitView();
  }, [centreNow, fitView]);

  // --- layout: positions for the shown nodes ---------------------------------------------------------------------
  useEffect(() => {
    layoutRef.current = null;
    const n = model.nodes.length;
    posX.current = new Float64Array(n).fill(NaN);
    posY.current = new Float64Array(n).fill(NaN);
    moved.current = false;
    if (mode === "map") return; // the map layout is drawn by NeighbourGraphMap
    const local = new Map<number, number>();
    vis.nodeIdx.forEach((gi, li) => local.set(gi, li));
    const links = vis.edges.map((e) => ({ a: local.get(e.a) as number, b: local.get(e.b) as number }));
    const lay = createLayout(vis.nodeIdx.length, links, { linkDistance: 26, charge: -34 });
    layoutRef.current = lay;
    const publish = () => {
      vis.nodeIdx.forEach((gi, li) => {
        posX.current[gi] = lay.xs[li];
        posY.current[gi] = lay.ys[li];
      });
      autoView();
    };
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      lay.step(400);
      publish();
      requestDraw();
      return;
    }
    let raf = 0;
    let cancelled = false;
    const frame = () => {
      if (cancelled) return;
      const t0 = performance.now();
      do {
        lay.step(2);
      } while (!lay.settled && performance.now() - t0 < FRAME_BUDGET_MS);
      publish();
      draw();
      if (!lay.settled) raf = requestAnimationFrame(frame); // settled: the loop ends and nothing runs again
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
    // a resize re-fits below without restarting the simulation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, vis, mode, draw, autoView, requestDraw]);

  // --- size and redraw triggers ----------------------------------------------------------------------------------
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const apply = () => {
      const w = Math.max(280, Math.floor(el.clientWidth));
      const h = Math.max(380, Math.min(720, Math.floor(window.innerHeight * 0.7)));
      setSize((s) => (s.w === w && s.h === h ? s : { w, h }));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(size.w * dpr);
    canvas.height = Math.floor(size.h * dpr);
    if (mode === "graph") autoView();
    requestDraw();
  }, [size, mode, autoView, requestDraw]);

  useEffect(() => {
    hoverRef.current = hover;
    selRef.current = selected;
    requestDraw();
  }, [hover, selected, theme, requestDraw]);

  // --- pointer: hover, pan, click, zoom --------------------------------------------------------------------------
  const hitTest = useCallback((px: number, py: number): number | null => {
    const { vis: v, maxDegree: maxDeg } = live.current;
    const { k, tx, ty } = view.current;
    const xs = posX.current, ys = posY.current;
    let best: number | null = null;
    let bestD = Infinity;
    for (const i of v.nodeIdx) {
      if (!Number.isFinite(xs[i])) continue;
      const dx = xs[i] * k + tx - px, dy = ys[i] * k + ty - py;
      const d = Math.sqrt(dx * dx + dy * dy);
      const reach = nodeRadius(v.degree[i], maxDeg) * Math.min(2, Math.max(0.8, Math.pow(k, 0.35))) + 4;
      if (d <= reach && d < bestD) {
        best = i;
        bestD = d;
      }
    }
    return best;
  }, []);

  const drag = useRef<{ x: number; y: number; tx: number; ty: number; moved: boolean } | null>(null);

  const placeTip = useCallback((px: number, py: number) => {
    const tip = tipRef.current;
    if (!tip) return;
    const w = live.current.size.w;
    const left = px + 16 + 300 > w ? Math.max(4, px - 16 - tip.offsetWidth) : px + 16;
    tip.style.left = `${left}px`;
    tip.style.top = `${Math.max(4, py - 8)}px`;
  }, []);

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left, py = e.clientY - rect.top;
    const d = drag.current;
    if (d) {
      if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 4) d.moved = true;
      if (d.moved) {
        view.current = { ...view.current, tx: d.tx + (e.clientX - d.x), ty: d.ty + (e.clientY - d.y) };
        moved.current = true;
        requestDraw();
      }
      return;
    }
    const hit = hitTest(px, py);
    if (hit !== hoverRef.current) setHover(hit);
    placeTip(px, py);
  };
  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, tx: view.current.tx, ty: view.current.ty, moved: false };
  };
  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    drag.current = null;
    if (d && !d.moved) {
      const rect = e.currentTarget.getBoundingClientRect();
      const hit = hitTest(e.clientX - rect.left, e.clientY - rect.top);
      if (hit !== null) navigate(`#/nodes/${encodeURIComponent(live.current.model.nodes[hit].id)}`);
    }
  };
  const onPointerLeave = () => {
    if (!drag.current && hoverRef.current !== null) setHover(null);
  };

  const zoomBy = useCallback((factor: number, px: number, py: number) => {
    const { k, tx, ty } = view.current;
    const nk = Math.min(12, Math.max(0.05, k * factor));
    view.current = { k: nk, tx: px - ((px - tx) * nk) / k, ty: py - ((py - ty) * nk) / k };
    moved.current = true;
    requestDraw();
  }, [requestDraw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      zoomBy(Math.exp(-e.deltaY * 0.0015), e.clientX - rect.left, e.clientY - rect.top);
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, [zoomBy, mode]);

  // --- search ----------------------------------------------------------------------------------------------------
  const withLinks = useMemo(() => model.nodes.map((_, i) => i).filter((i) => model.nodes[i].links > 0), [model]);
  const results = useMemo(() => searchNodes(model.nodes, withLinks, query, 8), [model, withLinks, query]);
  const [showResults, setShowResults] = useState(false);

  const pick = (idx: number) => {
    const need = minCountToShow(model, idx, minCount);
    const restarts = need !== null && need !== minCount; // the node's links are weaker than the filter: lower it
    if (restarts) setMinCount(need);
    setSelected(idx);
    setQuery(model.nodes[idx].name);
    setShowResults(false);
    moved.current = false;
    centreOn.current = idx; // the layout centres on it when it has a position
    if (!restarts && centreNow(idx)) requestDraw();
  };

  const clearSelection = () => {
    centreOn.current = null;
    setSelected(null);
    setQuery("");
  };

  // --- words and legend ------------------------------------------------------------------------------------------
  const roles = useMemo(() => {
    const seen = new Set<MeshCoreRole>();
    for (const i of vis.nodeIdx) seen.add(normalizeMeshCoreRole(model.nodes[i].role));
    return [...seen];
  }, [model, vis]);
  const unplaced = vis.nodeIdx.filter((i) => model.nodes[i].lat === null || model.nodes[i].lon === null).length;
  const shown = hover !== null ? hover : null;
  const stopIdx = Math.max(0, stops.indexOf(minCount) === -1 ? stops.findIndex((s) => s >= minCount) : stops.indexOf(minCount));

  if (model.edges.length === 0) return <div className="node-detail-note">{NO_LINKS_TEXT}</div>;

  return (
    <div className="ng-root">
      <div className="node-list-toolbar ng-controls">
        <div className="theme-toggle" role="group" aria-label="Layout" title={LAYOUT_TIP}>
          <button type="button" data-active={mode === "graph"} onClick={() => setMode("graph")}>Graph</button>
          <button type="button" data-active={mode === "map"} onClick={() => setMode("map")}>Map</button>
        </div>
        <label className="ng-slider" title={MIN_OBS_TIP}>
          <span>Minimum observations</span>
          <input
            type="range"
            min={0}
            max={stops.length - 1}
            step={1}
            value={stopIdx}
            onChange={(e) => setMinCount(stops[Number(e.target.value)])}
            aria-label="Minimum observations"
          />
          <strong>{minCount}</strong>
        </label>
        <div className="ng-search">
          <input
            type="search"
            className="node-list-search"
            placeholder="Find a node"
            aria-label="Find a node"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && results.length > 0) pick(results[0]);
              if (e.key === "Escape") setShowResults(false);
            }}
          />
          {showResults && results.length > 0 && (
            <ul className="ng-results">
              {results.map((i) => (
                <li key={model.nodes[i].id}>
                  <button type="button" onClick={() => pick(i)}>
                    <span className="ng-dot" style={{ background: roleColor(model.nodes[i].role) }} />
                    {model.nodes[i].name}
                    <span className="ng-muted"> {model.nodes[i].links} links</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {selected !== null && (
          <button type="button" className="ng-clear" onClick={clearSelection}>Clear selection</button>
        )}
        <span className="ng-count">{countText(vis.nodeIdx.length, vis.edges.length, model.nodes.length, model.edges.length)}</span>
      </div>

      <div className="ng-canvas-wrap" ref={wrapRef} style={{ height: size.h }}>
        {mode === "map" ? (
          <NeighbourGraphMap key={theme} model={model} vis={vis} theme={theme} colorOf={roleColor} selected={selected} height={size.h} />
        ) : (
          <>
            <canvas
              ref={canvasRef}
              className="ng-canvas"
              style={{ width: size.w, height: size.h, cursor: hover !== null ? "pointer" : "grab" }}
              onPointerMove={onPointerMove}
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerLeave}
              aria-label="Neighbour graph"
              role="img"
            />
            <div className="ng-zoom">
              <button type="button" aria-label="Zoom in" onClick={() => zoomBy(1.4, size.w / 2, size.h / 2)}>+</button>
              <button type="button" aria-label="Zoom out" onClick={() => zoomBy(1 / 1.4, size.w / 2, size.h / 2)}>−</button>
              <button
                type="button"
                aria-label="Fit to view"
                onClick={() => {
                  moved.current = false;
                  centreOn.current = null;
                  fitView();
                  requestDraw();
                }}
              >
                Fit
              </button>
            </div>
            <div className="ng-tip" ref={tipRef} style={{ display: shown === null ? "none" : "block" }}>
              {shown !== null && <NodeTip model={model} vis={vis} idx={shown} />}
            </div>
          </>
        )}
      </div>

      <div className="ng-legend">
        {roles.map((r) => (
          <span key={r} className="ng-legend-item">
            <span className="ng-dot" style={{ background: colorFor(roleShade("meshcore", MESHCORE_ROLE_CATEGORY[r]), theme) }} />
            {MESHCORE_ROLE_LABEL[r]}
          </span>
        ))}
        <span className="ng-muted">Bigger dot: more links. Thicker, darker line: seen more often.</span>
      </div>
      {mode === "map" && unplaced > 0 && <div className="node-detail-note">{unplacedText(unplaced)}</div>}
      {payload.counts.dropped_edges > 0 && <div className="node-detail-note">{droppedText(payload.counts.dropped_edges)}</div>}
    </div>
  );
}
